
import {buildExportFilename, sanitizeFilenamePart} from "./exportFilename.js";

import {t} from "../i18n/index.js";
// Tynd facade mellem en host-UI (Geonote-frontendens "Gem registreringer"-
// dialog i Vue) og den EKSISTERENDE eksportmotor. Ingen ny eksporter: al
// formatlogik bor stadig i ExportRegistry's exporters og csv/-pipelinen.
// Facaden gør kun tre ting:
//   1) beskriver de registrerede formater som ren data (så UI'et ikke har en
//      parallel, hardcoded formatliste og ikke kender konkrete exporters),
//   2) slår feature-id'er op i FeatureManager (autoriteten for registreringer;
//      mapper/FolderManager eksporteres aldrig som geometri) og kalder den
//      valgte exporter med rækkefølgen fra kalderen,
//   3) samler filnavnet efter den eksisterende konvention (exportFilename.js).
// Skjulte registreringer er IKKE et filter: er et id valgt, eksporteres den.
//
// Alt her er ren logik uden DOM/THREE (Level-1-testbar); DigitizationModule
// binder den til det rigtige register, CSV-profillager og viewer.

// Punktskynavn (og evt. mappenavn) -> ét filnavnsstem. Hver del saneres for sig,
// så en mappe kaldet "a/b" ikke bliver til en undermappe i filnavnet.
export function composeExportName(pointcloudName, label = null){
	const parts = [sanitizeFilenamePart(pointcloudName)];
	if(label && String(label).trim()) parts.push(sanitizeFilenamePart(label));
	return parts.join("_");
}

export function previewExportFilename(exporter, {pointcloudName = null, label = null, exportDate = new Date()} = {}){
	return buildExportFilename(composeExportName(pointcloudName, label), exporter.extension, exportDate);
}

// Formaterne som ren data. Skjulte/deaktiverede (stubs) udelades.
// crsHandling: hvordan formatet håndterer koordinatsystemet - 'embedded' (skrives
// i filen), 'unsupported' (kan ikke skrives af den nuværende motor) eller
// 'none' (formatet har ingen CRS-angivelse).
export function buildExportModel(registry){
	return {
		formats: registry.list().filter((e) => !e.disabled).map((e) => ({
			id: e.id,
			name: t(e.name),
			extension: e.extension,
			description: t(e.description || ""),
			crsHandling: e.crsHandling || "none",
			// formatspecifikke indstillinger (kun hvis exporteren selv beskriver nogen)
			options: typeof e.options === "function" ? e.options() : [],
		})),
	};
}

// context: {featureManager, pointcloudName, label, epsg, projection, exportDate}
// Kaster (uændret) hvis exporteren fejler - kalderen viser fejlen; intet her
// ændrer FeatureManager eller andre stores.
export async function runExport(registry, {formatId, featureIds, options = {}, context = {}}){
	const exporter = registry.get(formatId);
	if(!exporter || exporter.disabled) throw new Error(t("Ukendt eller utilgængeligt eksportformat."));

	const fm = context.featureManager;
	const features = [];
	for(const id of featureIds || []){
		const feature = fm ? fm.get(id) : null;
		if(feature) features.push(feature); // slettede id'er springes over
	}
	if(features.length === 0) throw new Error(t("Ingen registreringer at gemme."));

	const exportDate = context.exportDate || new Date();
	let exportContext = {
		projection: context.projection || null,
		epsg: context.epsg || null,
		pointcloudName: composeExportName(context.pointcloudName, context.label),
		exportDate,
	};
	if(typeof exporter.prepareContext === "function") exportContext = exporter.prepareContext(options, exportContext);

	const result = await exporter.export(features, exportContext);
	return {...result, count: features.length};
}
